import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { icons } from '../../components/ui/Icons';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Tabs, TabPanel, type TabItem } from '../../components/ui/Tabs';
import { Avatar } from '../../components/ui/Avatar';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorState } from '../../components/ui/ErrorState';
import { SkeletonText } from '../../components/ui/Skeleton';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { UserWithDetails, EffectivePermission, Permission, UserPermissionOverride } from '../../types/auth';
import { formatDate } from '../../lib/date';

const API_BASE = '/api/admin';

type DetailTab = 'overview' | 'permissions' | 'roles' | 'overrides' | 'activity';

const SOURCE_LABELS: Record<EffectivePermission['source'], string> = {
  discord_role: 'Discord-Rolle',
  user_override: 'Persönliche Ausnahme',
  admin_role: 'Admin-Rolle',
};

const PERMISSION_GROUPS = [
  'Dashboard',
  'Patienten',
  'Medizin',
  'Personal',
  'Organisation',
  'Dokumente',
  'Kommunikation',
  'Verwaltung',
  'System',
] as const;

type PermissionGroup = (typeof PERMISSION_GROUPS)[number];

const PERMISSION_CATEGORY_MAP: Record<string, PermissionGroup> = {
  Dashboard: 'Dashboard',
  'Patienten & Stationen': 'Patienten',
  Pflege: 'Patienten',
  Medizin: 'Medizin',
  Personal: 'Personal',
  Organisation: 'Organisation',
  Wissen: 'Dokumente',
  Kommunikation: 'Kommunikation',
  'Sicherheit & Notfall': 'Organisation',
  Betrieb: 'Organisation',
  Verwaltung: 'Verwaltung',
  System: 'System',
};

function getPermissionGroup(permission: Permission): PermissionGroup {
  return PERMISSION_CATEGORY_MAP[permission.category] || 'System';
}

function groupEffectivePermissions(permissions: EffectivePermission[]): Record<PermissionGroup, EffectivePermission[]> {
  const groups = Object.fromEntries(
    PERMISSION_GROUPS.map((group) => [group, [] as EffectivePermission[]]),
  ) as Record<PermissionGroup, EffectivePermission[]>;

  permissions.forEach((permission) => {
    groups[getPermissionGroup(permission as unknown as Permission)].push(permission);
  });

  Object.values(groups).forEach((group) => {
    group.sort((a, b) => (a.permissionName || a.permissionKey).localeCompare(b.permissionName || b.permissionKey, 'de-DE'));
  });

  return groups;
}

function groupOverrides(overrides: UserPermissionOverride[]): Record<PermissionGroup, UserPermissionOverride[]> {
  const groups = Object.fromEntries(
    PERMISSION_GROUPS.map((group) => [group, [] as UserPermissionOverride[]]),
  ) as Record<PermissionGroup, UserPermissionOverride[]>;

  overrides.forEach((override) => {
    groups[getPermissionGroup(override as unknown as Permission)].push(override);
  });

  Object.values(groups).forEach((group) => {
    group.sort((a, b) => (a.permissionName || a.permissionKey).localeCompare(b.permissionName || b.permissionKey, 'de-DE'));
  });

  return groups;
}

interface ApiErrorBody {
  message?: string;
  error?: string;
}

class RequestError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'RequestError';
    this.status = status;
  }
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as ApiErrorBody;
      message = body.message || body.error || message;
    } catch {
      message = `HTTP ${response.status}`;
    }
    throw new RequestError(response.status, message);
  }

  const text = await response.text();
  if (!text) return undefined as T;

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('Die API hat keine gültigen JSON-Daten zurückgegeben.');
  }
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof RequestError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function isEffectivePermission(value: unknown): value is EffectivePermission {
  const item = value as Partial<EffectivePermission>;
  return (
    typeof item?.permissionId === 'string' &&
    typeof item?.permissionKey === 'string' &&
    (item.effect === 'ALLOW' || item.effect === 'DENY') &&
    (item.source === 'discord_role' || item.source === 'user_override' || item.source === 'admin_role')
  );
}

function normalizeUserDetail(value: unknown): UserWithDetails {
  const data = value as Partial<UserWithDetails> & {
    permissionDetails?: unknown;
    effectivePermissions?: unknown;
    permissionKeys?: unknown;
    overrides?: unknown;
  };
  const rawRoleSummaries = (value as { discordRoleSummaries?: unknown }).discordRoleSummaries;
  const rawRoles = rawRoleSummaries ?? (value as { discordRoles?: unknown }).discordRoles;
  const rawPermissionKeys = (value as { permissionKeys?: unknown }).permissionKeys;
  const rawOverrides = (value as { overrides?: unknown }).overrides;
  const details = Array.isArray(data.permissionDetails)
    ? data.permissionDetails.filter(isEffectivePermission)
    : [];
  const rawEffective = Array.isArray(data.effectivePermissions)
    ? data.effectivePermissions.filter(isEffectivePermission)
    : [];
  const overrides = Array.isArray(rawOverrides)
    ? rawOverrides.filter((item): item is UserPermissionOverride => Boolean(item) && typeof item === 'object')
    : [];

  return {
    ...normalizeUser(value),
    discordRoles: Array.isArray(rawRoles)
      ? rawRoles.map((role) => {
          if (typeof role === 'string') return { id: role, name: role };
          const item = role as Partial<{ id: string; name: string; discordRoleId: string }>;
          const id = item.id || item.discordRoleId || '';
          return { id, name: item.name || id || 'Unbekannte Rolle' };
        })
      : [],
    effectivePermissions: details.length > 0 ? details : rawEffective,
    permissionKeys: Array.isArray(rawPermissionKeys)
      ? rawPermissionKeys.filter((key): key is string => typeof key === 'string')
      : (details.length > 0 ? details : rawEffective).map((permission) => permission.permissionKey),
    overrides,
  };
}

function normalizeUser(value: unknown): UserWithDetails {
  const data = value as Partial<UserWithDetails>;
  const rawRoleSummaries = (value as { discordRoleSummaries?: unknown }).discordRoleSummaries;
  const rawRoles = rawRoleSummaries ?? (value as { discordRoles?: unknown }).discordRoles;
  const rawPermissionKeys = (value as { permissionKeys?: unknown }).permissionKeys;

  return {
    id: data.id || '',
    discordUserId: data.discordUserId || '',
    discordUsername: data.discordUsername || '',
    discordDisplayName: data.discordDisplayName ?? null,
    discordAvatar: data.discordAvatar ?? null,
    email: data.email ?? null,
    firstName: data.firstName ?? null,
    lastName: data.lastName ?? null,
    personnelNumber: data.personnelNumber ?? null,
    departmentId: data.departmentId ?? null,
    departmentName: data.departmentName ?? null,
    positionId: data.positionId ?? null,
    position: data.position ?? null,
    isActive: data.isActive ?? false,
    lastLoginAt: data.lastLoginAt ?? null,
    createdAt: data.createdAt || '',
    updatedAt: data.updatedAt || '',
    discordRoles: Array.isArray(rawRoles)
      ? rawRoles.map((role) => {
          if (typeof role === 'string') return { id: role, name: role };
          const item = role as Partial<{ id: string; name: string; discordRoleId: string }>;
          const id = item.id || item.discordRoleId || '';
          return { id, name: item.name || id || 'Unbekannte Rolle' };
        })
      : [],
    effectivePermissions: [],
    permissionKeys: Array.isArray(rawPermissionKeys)
      ? rawPermissionKeys.filter((key): key is string => typeof key === 'string')
      : [],
    overrides: [],
  };
}

const DETAIL_TABS: TabItem[] = [
  { id: 'overview', label: 'Übersicht', icon: 'User' },
  { id: 'permissions', label: 'Effektive Berechtigungen', icon: 'Key' },
  { id: 'roles', label: 'Discord-Rollen', icon: 'Shield' },
  { id: 'overrides', label: 'Ausnahmen', icon: 'ShieldAlert' },
  { id: 'activity', label: 'Aktivität', icon: 'Activity' },
];

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');

  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);

  const [overridePermissionId, setOverridePermissionId] = useState('');
  const [overrideEffect, setOverrideEffect] = useState<'ALLOW' | 'DENY'>('ALLOW');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [overrideMessage, setOverrideMessage] = useState<{ variant: 'success' | 'error'; text: string } | null>(null);

  const requestRef = useRef<AbortController | null>(null);

  const fetchUser = useCallback(async () => {
    if (!id) return;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const data = await requestJson<unknown>(`/users/${encodeURIComponent(id)}`, { signal: controller.signal });
      setUser(normalizeUserDetail(data));
    } catch (error) {
      if (!isAbortError(error)) {
        setError(getErrorMessage(error, 'Benutzerdetails konnten nicht geladen werden.'));
      }
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      setLoading(false);
    }
  }, [id]);

  const fetchPermissions = useCallback(async () => {
    try {
      const data = await requestJson<unknown[]>(`/permissions`);
      setAvailablePermissions(Array.isArray(data) ? data.map((item) => {
        const permission = item as Partial<Permission>;
        return {
          id: permission.id || '',
          key: permission.key || '',
          name: permission.name || permission.key || 'Unbenannte Berechtigung',
          category: permission.category || 'System',
          description: typeof permission.description === 'string' ? permission.description : null,
        };
      }) : []);
    } catch (error) {
      console.error('Failed to load permissions:', error);
    }
  }, []);

  const updateUserStatus = useCallback(async (isActive: boolean) => {
    if (!user) return;
    try {
      await requestJson<{ success: boolean }>(`/users/${encodeURIComponent(user.id)}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive }),
      });
      await fetchUser();
    } catch (error) {
      alert(getErrorMessage(error, 'Benutzerstatus konnte nicht geändert werden.'));
    }
  }, [user, fetchUser]);

  const addOverride = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user || !overridePermissionId || !overrideEffect) {
      setOverrideMessage({ variant: 'error', text: 'Bitte Berechtigung und Effekt auswählen.' });
      return;
    }

    setOverrideSubmitting(true);
    setOverrideMessage(null);
    try {
      await requestJson<{ success: boolean }>(`/users/${encodeURIComponent(user.id)}/overrides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          permissionId: overridePermissionId,
          effect: overrideEffect,
          reason: overrideReason.trim() || null,
        }),
      });
      await Promise.all([fetchUser(), fetchPermissions()]);
      setOverridePermissionId('');
      setOverrideReason('');
      setOverrideMessage({ variant: 'success', text: 'Berechtigungsausnahme wurde gespeichert.' });
    } catch (error) {
      setOverrideMessage({ variant: 'error', text: getErrorMessage(error, 'Berechtigungsausnahme konnte nicht gespeichert werden.') });
    } finally {
      setOverrideSubmitting(false);
    }
  }, [user, overridePermissionId, overrideEffect, overrideReason, fetchUser, fetchPermissions]);

  const removeOverride = useCallback(async (permissionId: string) => {
    if (!user) return;
    if (!window.confirm('Diese Berechtigungsausnahme wirklich entfernen?')) return;

    try {
      await requestJson<{ success: boolean }>(`/users/${encodeURIComponent(user.id)}/overrides/${encodeURIComponent(permissionId)}`, {
        method: 'DELETE',
      });
      await fetchUser();
    } catch (error) {
      alert(getErrorMessage(error, 'Berechtigungsausnahme konnte nicht entfernt werden.'));
    }
  }, [user, fetchUser]);

  useEffect(() => {
    fetchUser();
    fetchPermissions();
    return () => requestRef.current?.abort();
  }, [fetchUser, fetchPermissions]);

  const breadcrumbItems = [
    { label: 'Administration', href: '/admin' },
    { label: 'Benutzer & Zugriff', href: '/admin/access' },
    { label: 'Benutzer', href: '/admin/access/users' },
    { label: user?.discordDisplayName || user?.discordUsername || 'Lädt...', current: true },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={breadcrumbItems} />
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 flex-shrink-0 rounded-full bg-neutral-200 animate-pulse dark:bg-neutral-700" />
          <div>
            <SkeletonText className="h-6 w-48" />
            <SkeletonText className="h-4 w-64 mt-2" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={breadcrumbItems} />
        <ErrorState description={error || 'Benutzer nicht gefunden'} onRetry={fetchUser} />
      </div>
    );
  }

  const userName = user.discordDisplayName || user.discordUsername;
  const avatarSrc = user.discordUserId && user.discordAvatar
    ? `https://cdn.discordapp.com/avatars/${encodeURIComponent(user.discordUserId)}/${encodeURIComponent(user.discordAvatar)}.png`
    : null;

  const groupedEffectivePermissions = useMemo(
    () => groupEffectivePermissions(user.effectivePermissions),
    [user.effectivePermissions]
  );

  const groupedOverrides = useMemo(
    () => groupOverrides(user.overrides),
    [user.overrides]
  );

  return (
    <div className="space-y-6">
      <Breadcrumb items={breadcrumbItems} />

      {/* User Header */}
      <Card className="border-neutral-200 dark:border-neutral-700">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <Avatar src={avatarSrc} name={userName} size="xl" />
              <div>
                <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{userName}</h1>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">@{user.discordUsername}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant={user.isActive ? 'medical' : 'gray'} className="text-sm">
                {user.isActive ? 'Aktiv' : 'Inaktiv'}
              </Badge>
              <Button variant="outline" size="sm" leftIcon={user.isActive ? 'UserX' : 'UserCheck'} onClick={() => updateUserStatus(!user.isActive)}>
                {user.isActive ? 'Deaktivieren' : 'Aktivieren'}
              </Button>
              <Button variant="ghost" size="sm" leftIcon="ArrowLeft" onClick={() => navigate('/admin/access/users')}>
                Zurück zur Liste
              </Button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 text-sm">
            <dl>
              <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">E-Mail</dt>
              <dd className="mt-1 font-medium text-neutral-900 dark:text-white">{user.email || '-'}</dd>
            </dl>
            <dl>
              <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Personalnummer</dt>
              <dd className="mt-1 font-medium text-neutral-900 dark:text-white">{user.personnelNumber || '-'}</dd>
            </dl>
            <dl>
              <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Position</dt>
              <dd className="mt-1 font-medium text-neutral-900 dark:text-white">{user.position || '-'}</dd>
            </dl>
            <dl>
              <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Abteilung</dt>
              <dd className="mt-1 font-medium text-neutral-900 dark:text-white">{user.departmentName || '-'}</dd>
            </dl>
            <dl className="sm:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Discord-ID</dt>
              <dd className="mt-1 font-medium text-neutral-900 dark:text-white font-mono text-xs">{user.discordUserId || '-'}</dd>
            </dl>
            <dl className="sm:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Letzter Login</dt>
              <dd className="mt-1 font-medium text-neutral-900 dark:text-white">{formatDate(user.lastLoginAt, true)}</dd>
            </dl>
            <dl>
              <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Erstellt</dt>
              <dd className="mt-1 font-medium text-neutral-900 dark:text-white">{formatDate(user.createdAt)}</dd>
            </dl>
            <dl>
              <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Aktualisiert</dt>
              <dd className="mt-1 font-medium text-neutral-900 dark:text-white">{formatDate(user.updatedAt, true)}</dd>
            </dl>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs
        tabs={DETAIL_TABS}
        activeTab={activeTab}
        onChange={(tab) => setActiveTab(tab as DetailTab)}
        variant="pills"
        className="flex flex-wrap gap-1"
      />

      {/* Overview Tab */}
      <TabPanel id="overview" active={activeTab === 'overview'}>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Discord Roles */}
          <Card className="border-neutral-200 dark:border-neutral-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <icons.Shield className="h-5 w-5" aria-hidden="true" />
                Discord-Rollen ({user.discordRoles.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {user.discordRoles.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {user.discordRoles.map((role) => (
                    <Badge key={role.id} variant="outline" className="gap-1">
                      {role.name}
                      {role.color && (
                        <span
                          className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: `#${role.color}` }}
                          aria-hidden="true"
                        />
                      )}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Keine Discord-Rollen zugewiesen.</p>
              )}
            </CardContent>
          </Card>

          {/* Stats */}
          <Card className="border-neutral-200 dark:border-neutral-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <icons.Activity className="h-5 w-5" aria-hidden="true" />
                Berechtigungen & Ausnahmen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-4 rounded-lg bg-primary-50 dark:bg-primary-900/20">
                  <p className="text-3xl font-bold text-primary-700 dark:text-primary-300">{user.effectivePermissions.length}</p>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Effektive Berechtigungen</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                  <p className="text-3xl font-bold text-amber-700 dark:text-amber-300">{user.overrides.length}</p>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Persönliche Ausnahmen</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </TabPanel>

      {/* Permissions Tab */}
      <TabPanel id="permissions" active={activeTab === 'permissions'}>
        <Card className="border-neutral-200 dark:border-neutral-700">
          <CardHeader>
            <CardTitle>Effektive Berechtigungen</CardTitle>
            <CardDescription>
              Alle Berechtigungen, die diesem Benutzer über Rollen und Ausnahmen zustehen.
              <Badge variant="primary" className="ml-2">{user.effectivePermissions.length}</Badge>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {user.effectivePermissions.length === 0 ? (
              <EmptyState icon="Key" title="Keine effektiven Berechtigungen" description="Diesem Benutzer sind aktuell keine Berechtigungen zugewiesen." />
            ) : (
              <div className="space-y-4">
                {PERMISSION_GROUPS.map((group) => {
                  const perms = groupedEffectivePermissions[group];
                  if (!perms || perms.length === 0) return null;
                  return (
                    <div key={group} className="space-y-2">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{group}</h4>
                      <div className="flex flex-wrap gap-2">
                        {perms.map((permission, index) => (
                          <Badge
                            key={`${permission.permissionKey}-${permission.sourceId}-${index}`}
                            variant={permission.effect === 'ALLOW' ? 'primary' : 'emergency'}
                            size="sm"
                            className={cn('gap-1', permission.source === 'admin_role' && 'ring-2 ring-amber-400/70')}
                            title={`${SOURCE_LABELS[permission.source]} · ${permission.effect}`}
                          >
                            {permission.permissionName || permission.permissionKey}
                            <span className="opacity-75">· {SOURCE_LABELS[permission.source]}</span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Roles Tab */}
      <TabPanel id="roles" active={activeTab === 'roles'}>
        <Card className="border-neutral-200 dark:border-neutral-700">
          <CardHeader>
            <CardTitle>Discord-Rollen</CardTitle>
            <CardDescription>Dem Benutzer zugewiesene Discord-Rollen.</CardDescription>
          </CardHeader>
          <CardContent>
            {user.discordRoles.length === 0 ? (
              <EmptyState icon="Shield" title="Keine Discord-Rollen" description="Diesem Benutzer sind aktuell keine Discord-Rollen zugewiesen." />
            ) : (
              <div className="space-y-3">
                {user.discordRoles.map((role) => (
                  <div key={role.id} className="flex items-center gap-3 p-3 rounded-lg border border-neutral-200 dark:border-neutral-700">
                    <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center dark:bg-primary-900/30 dark:text-primary-300">
                      <icons.Shield className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-neutral-900 dark:text-white truncate">{role.name}</p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">ID: {role.id}</p>
                    </div>
                    {role.color && (
                      <span
                        className="h-4 w-4 rounded-full flex-shrink-0 border border-neutral-300 dark:border-neutral-600"
                        style={{ backgroundColor: `#${role.color}` }}
                        aria-label={`Rollenfarbe: #${role.color}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Overrides Tab */}
      <TabPanel id="overrides" active={activeTab === 'overrides'}>
        <Card className="border-neutral-200 dark:border-neutral-700">
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle>Persönliche Ausnahmen (Overrides)</CardTitle>
              <CardDescription>
                Individuelle Berechtigungsausnahmen für diesen Benutzer.
                <Badge variant="primary" className="ml-2">{user.overrides.length}</Badge>
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Add Override Form */}
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-900/30">
              <form onSubmit={addOverride} className="space-y-4">
                <h4 className="font-medium text-neutral-900 dark:text-white">Neue Ausnahme hinzufügen</h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_10rem_10rem_12rem]">
                  <Select
                    label="Berechtigung"
                    value={overridePermissionId}
                    onChange={(event) => setOverridePermissionId(event.target.value)}
                    options={availablePermissions.map((p: Permission) => ({ value: p.id, label: `${p.name} (${p.key})` }))}
                    placeholder="Berechtigung auswählen"
                    required
                  />
                  <Select
                    label="Effekt"
                    value={overrideEffect}
                    onChange={(event) => setOverrideEffect(event.target.value as 'ALLOW' | 'DENY')}
                    options={[
                      { value: 'ALLOW', label: 'Erlauben (ALLOW)' },
                      { value: 'DENY', label: 'Verbieten (DENY)' },
                    ]}
                  />
                  <Input
                    label="Begründung (optional)"
                    placeholder="Grund für die Ausnahme..."
                    value={overrideReason}
                    onChange={(event) => setOverrideReason(event.target.value)}
                  />
                  <Button type="submit" variant="primary" leftIcon="Plus" loading={overrideSubmitting} disabled={overrideSubmitting || !overridePermissionId}>
                    Ausnahme speichern
                  </Button>
                </div>
                {overrideMessage && (
                  <Alert variant={overrideMessage.variant} dismissible onDismiss={() => setOverrideMessage(null)}>
                    {overrideMessage.text}
                  </Alert>
                )}
              </form>
            </div>

            {/* Existing Overrides */}
            {user.overrides.length === 0 ? (
              <EmptyState icon="ShieldAlert" title="Keine Ausnahmen" description="Für diesen Benutzer sind keine persönlichen Berechtigungsausnahmen konfiguriert." />
            ) : (
              <div className="space-y-4">
                {PERMISSION_GROUPS.map((group) => {
                  const perms = groupedOverrides[group];
                  if (!perms || perms.length === 0) return null;
                  return (
                    <div key={group} className="space-y-2">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{group}</h4>
                      <div className="space-y-2">
                        {perms.map((override) => (
                          <div key={override.permissionId} className="flex items-center justify-between gap-4 p-3 rounded-lg border border-neutral-200 dark:border-neutral-700">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant={override.effect === 'ALLOW' ? 'primary' : 'emergency'} size="sm">
                                  {override.effect}
                                </Badge>
                                <span className="font-medium text-neutral-900 dark:text-white">{override.permissionName || override.permissionKey}</span>
                              </div>
                              {override.reason && (
                                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{override.reason}</p>
                              )}
                            </div>
                            <Button variant="danger" size="sm" leftIcon="Trash2" onClick={() => removeOverride(override.permissionId)}>
                              Entfernen
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Activity Tab - placeholder for audit logs related to this user */}
      <TabPanel id="activity" active={activeTab === 'activity'}>
        <Card className="border-neutral-200 dark:border-neutral-700">
          <CardHeader>
            <CardTitle>Aktivität</CardTitle>
            <CardDescription>Admin-Aktivitäten im Zusammenhang mit diesem Benutzer.</CardDescription>
          </CardHeader>
          <CardContent>
            <EmptyState icon="Activity" title="Aktivitätsprotokoll" description="Die Anzeige von benutzerbezogenen Audit-Einträgen wird in einer späteren Version implementiert." />
          </CardContent>
        </Card>
      </TabPanel>
    </div>
  );
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';
}